import { useEffect, useRef } from "react"
import {
  createChart,
  ColorType,
  CrosshairMode,
  CandlestickSeries,
  createSeriesMarkers,
  type IChartApi,
  type ISeriesApi,
  type IPriceLine,
  type ISeriesMarkersPluginApi,
  type Time,
} from "lightweight-charts"
import type { Candle, ChartMarker, ChartFocus } from "../types/chart"
import { cn } from "@/lib/utils"

export interface ChartViewTrade {
  entryTime: number
  exitTime: number
  dir: "BUY" | "SELL"
  entry: number
  exit: number
  pnl: number
}

export interface ChartViewProps {
  ohlc: Candle[]
  markers?: ChartMarker[]
  visibleCount?: number
  theme?: "light" | "dark"
  focus?: ChartFocus | null
  onClear?: () => void
  onInitCandleCount?: (total: number) => void
  /** Show the TradingView attribution logo in the corner. Defaults to true. */
  attributionLogo?: boolean
  /** Initial bar spacing in pixels (controls candle width). Library default is ~6. */
  barSpacing?: number
  className?: string
}

export function ChartView({
  ohlc,
  markers = [],
  visibleCount,
  theme = "light",
  focus = null,
  onClear,
  onInitCandleCount,
  attributionLogo = true,
  barSpacing,
  className,
}: ChartViewProps) {
  const isDark = theme === "dark"
  const containerRef = useRef<HTMLDivElement | null>(null)
  const chartRef = useRef<IChartApi | null>(null)
  const seriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null)
  const markersRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null)
  const tradeLinesRef = useRef<IPriceLine[]>([])
  const visible = visibleCount ?? ohlc.length

  useEffect(() => {
    onInitCandleCount?.(ohlc.length)
  }, [onInitCandleCount, ohlc.length])

  useEffect(() => {
    if (!containerRef.current) return
    const container = containerRef.current
    const bgColor = isDark ? "#1a1a1a" : "#ffffff"
    const textColor = isDark ? "#888888" : "#737373"
    const gridColor = isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.06)"
    const borderColor = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"
    const upColor = isDark ? "rgba(38,166,154,0.85)" : "rgba(22,163,74,0.85)"
    const downColor = isDark ? "rgba(242,54,69,0.85)" : "rgba(220,38,38,0.85)"
    const wickUp = isDark ? "rgba(38,166,154,0.6)" : "rgba(22,163,74,0.6)"
    const wickDown = isDark ? "rgba(242,54,69,0.6)" : "rgba(220,38,38,0.6)"
    const chart = createChart(container, {
      width: container.clientWidth,
      height: container.clientHeight || 300,
      layout: {
        background: { type: ColorType.Solid, color: bgColor },
        textColor,
        fontSize: 11,
        attributionLogo,
      },
      grid: { vertLines: { color: gridColor }, horzLines: { color: gridColor } },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor },
      localization: { priceFormatter: (p: number) => p.toFixed(4) },
      timeScale: {
        borderColor,
        timeVisible: true,
        secondsVisible: false,
        ...(barSpacing != null ? { barSpacing } : {}),
      },
    })
    const series = chart.addSeries(CandlestickSeries, {
      upColor,
      downColor,
      borderUpColor: upColor,
      borderDownColor: downColor,
      wickUpColor: wickUp,
      wickDownColor: wickDown,
    })
    series.setData(ohlc.slice(0, visible) as never)
    chartRef.current = chart
    seriesRef.current = series
    markersRef.current = createSeriesMarkers(series, [])
    const ro = new ResizeObserver(() => {
      if (containerRef.current) {
        chart.applyOptions({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight,
        })
      }
    })
    ro.observe(container)
    return () => {
      ro.disconnect()
      chart.remove()
      chartRef.current = null
      seriesRef.current = null
      markersRef.current = null
    }
  }, [isDark, ohlc, visible, attributionLogo, barSpacing])

  useEffect(() => {
    const series = seriesRef.current
    if (!series) return
    series.setData(ohlc.slice(0, visible) as never)
    const trade = focus?.trade as ChartViewTrade | null | undefined
    if (trade) {
      const focusedMarkers: ChartMarker[] = [
        {
          time: trade.entryTime,
          position: (trade.dir === "BUY" ? "belowBar" : "aboveBar") as ChartMarker["position"],
          color: trade.dir === "BUY" ? "#3db87a" : "#e05858",
          shape: (trade.dir === "BUY" ? "arrowUp" : "arrowDown") as ChartMarker["shape"],
          text: "",
        },
        {
          time: trade.exitTime,
          position: (trade.dir === "BUY" ? "aboveBar" : "belowBar") as ChartMarker["position"],
          color: trade.dir === "BUY" ? "#e05858" : "#3db87a",
          shape: (trade.dir === "BUY" ? "arrowDown" : "arrowUp") as ChartMarker["shape"],
          text: "",
        },
      ].sort((a, b) => a.time - b.time)
      markersRef.current?.setMarkers(focusedMarkers as never)
    } else {
      const visibleTimes = new Set(ohlc.slice(0, visible).map((c) => c.time))
      const visibleTradeMarkers = markers.filter((m) => visibleTimes.has(m.time))
      const all = [...visibleTradeMarkers].sort((a, b) => {
        const ta = typeof a.time === "number" ? a.time : Number(a.time)
        const tb = typeof b.time === "number" ? b.time : Number(b.time)
        return ta - tb
      })
      markersRef.current?.setMarkers(all as never)
    }
  }, [markers, ohlc, visible, focus])

  useEffect(() => {
    const chart = chartRef.current
    const series = seriesRef.current
    if (series) {
      tradeLinesRef.current.forEach((line) => {
        try {
          series.removePriceLine(line)
        } catch {
          /* noop */
        }
      })
      tradeLinesRef.current = []
    }
    if (!chart || !focus) return
    const timeScale = chart.timeScale()
    const logicalRange = timeScale.getVisibleLogicalRange()
    const candleIdx = ohlc.findIndex((c) => {
      const t = typeof c.time === "number" ? c.time : Number(c.time)
      return Math.abs(t - focus.time) < 60 * 60
    })
    if (candleIdx >= 0) {
      const width = logicalRange ? logicalRange.to - logicalRange.from : 60
      timeScale.setVisibleLogicalRange({
        from: candleIdx - width / 2,
        to: candleIdx + width / 2,
      })
    }
    const trade = focus.trade as ChartViewTrade | null
    if (trade && series) {
      const isProfit = trade.pnl >= 0
      const exitColor = isProfit ? "#26a69a" : "#f23645"
      series.applyOptions({ priceLineColor: "#555555", priceLineStyle: 2 })
      const entryLine = series.createPriceLine({
        price: trade.entry,
        color: "#e4e4e4",
        lineWidth: 1,
        lineStyle: 2,
        axisLabelVisible: true,
        title: `${trade.dir} entry`,
      })
      const exitLine = series.createPriceLine({
        price: trade.exit,
        color: exitColor,
        lineWidth: 1,
        lineStyle: 2,
        axisLabelVisible: true,
        title: isProfit
          ? `Exit | Profit of $${Math.abs(trade.pnl).toFixed(0)}`
          : `Exit | Loss of $${Math.abs(trade.pnl).toFixed(0)}`,
      })
      tradeLinesRef.current = [entryLine, exitLine]
    } else if (series) {
      series.applyOptions({ priceLineColor: "", priceLineStyle: 0 })
    }
  }, [focus, ohlc])

  useEffect(() => {
    const chart = chartRef.current
    if (!chart || !onClear) return
    let armed = false
    const armTimer = setTimeout(() => {
      armed = true
    }, 500)
    const handleClick = () => {
      if (armed) onClear()
    }
    chart.subscribeClick(handleClick)
    return () => {
      clearTimeout(armTimer)
      chart.unsubscribeClick(handleClick)
    }
  }, [isDark, ohlc, markers, onClear])

  return (
    <div className={cn("relative h-full w-full", className)}>
      <div ref={containerRef} className="absolute inset-0" />
    </div>
  )
}
